import { ArcLayer } from '@deck.gl/layers';
import type { ArcLayerProps } from '@deck.gl/layers';
import { Accessor, DefaultProps } from '@deck.gl/core';
import type { ShaderModule } from '@luma.gl/shadertools';

const uniformBlock = `\
uniform tripsUniforms {
  vec2 timeRange;
} trips;
`;

const fadeUniformBlock = `\
uniform packetFadeUniforms {
  float enabled;
  float midpoint;
  float range;
} packetFade;
`;

export type TripsProps = {
  timeRange: [number, number];
};

export const tripsUniforms = {
  name: 'trips',
  vs: uniformBlock,
  fs: uniformBlock,
  uniformTypes: {
    timeRange: 'vec2<f32>'
  }
} as const satisfies ShaderModule<TripsProps>;

export type FadeProps = {
  enabled: number;
  midpoint: number;
  range: number;
};

export const packetFadeUniforms = {
  name: 'packetFade',
  vs: fadeUniformBlock,
  fs: fadeUniformBlock,
  uniformTypes: {
    enabled: 'f32',
    midpoint: 'f32',
    range: 'f32'
  }
} as const satisfies ShaderModule<FadeProps>;

export type AnimatedArcLayerProps<DataT = any> = _AnimatedArcLayerProps<DataT> & ArcLayerProps<DataT>;

type _AnimatedArcLayerProps<DataT = any> = {
  getSourceTimestamp?: Accessor<DataT, number>;
  getTargetTimestamp?: Accessor<DataT, number>;
  timeRange?: [number, number];
  fadeEnabled?: boolean;
  fadeMidpoint?: number;
  fadeRange?: number;
};

const defaultProps: DefaultProps<_AnimatedArcLayerProps> = {
  getSourceTimestamp: { type: 'accessor', value: 0 },
  getTargetTimestamp: { type: 'accessor', value: 1 },
  timeRange: { type: 'array', compare: true, value: [0, 1] },
  fadeEnabled: { type: 'boolean', value: false },
  fadeMidpoint: { type: 'number', value: 0.5 },
  fadeRange: { type: 'number', value: 0.1 }
};

export class AnimatedArcLayer<DataT = any, ExtraProps = {}> extends ArcLayer<
  DataT,
  ExtraProps & Required<_AnimatedArcLayerProps>
> {
  layerName = 'AnimatedArcLayer';
  defaultProps = defaultProps;

  getShaders() {
    const shaders = super.getShaders();
    shaders.inject = {
      'vs:#decl': `\
in float instanceSourceTimestamp;
in float instanceTargetTimestamp;
out float vTimestamp;
out float vSourceTimestamp;
out float vTargetTimestamp;
`,
      'vs:#main-end': `\
vTimestamp = mix(instanceSourceTimestamp, instanceTargetTimestamp, segmentRatio);
vSourceTimestamp = instanceSourceTimestamp;
vTargetTimestamp = instanceTargetTimestamp;
`,
      'fs:#decl': `\
in float vTimestamp;
in float vSourceTimestamp;
in float vTargetTimestamp;
`,
      'fs:#main-start': `\
if (vTimestamp < trips.timeRange.x || vTimestamp > trips.timeRange.y) {
  discard;
}
`,
      'fs:DECKGL_FILTER_COLOR': `\
color.a *= (vTimestamp - trips.timeRange.x) / (trips.timeRange.y - trips.timeRange.x);
if (packetFade.enabled > 0.5) {
  float lifetime = max(0.0001, vTargetTimestamp - vSourceTimestamp);
  float age = clamp((trips.timeRange.y - vSourceTimestamp) / lifetime, 0.0, 1.0);
  float fade = 1.0 - smoothstep(packetFade.midpoint - packetFade.range, packetFade.midpoint, age);
  color.a *= fade;
}
`
    };
    shaders.modules = [...shaders.modules, tripsUniforms, packetFadeUniforms];
    return shaders;
  }

  initializeState() {
    super.initializeState();
    this.getAttributeManager().addInstanced({
      instanceSourceTimestamp: {
        size: 1,
        accessor: 'getSourceTimestamp'
      },
      instanceTargetTimestamp: {
        size: 1,
        accessor: 'getTargetTimestamp'
      }
    });
  }

  draw(params: Parameters<ArcLayer<DataT>['draw']>[0]) {
    const { timeRange, fadeEnabled, fadeMidpoint, fadeRange } = this.props;
    const tripsProps: TripsProps = { timeRange };
    const fadeProps: FadeProps = {
      enabled: fadeEnabled ? 1 : 0,
      midpoint: fadeMidpoint,
      range: fadeRange
    };
    const model = this.state.model!;
    model.shaderInputs.setProps({ trips: tripsProps, packetFade: fadeProps });
    super.draw(params);
  }
}
